import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import axios from "axios";
import AddWishListItemForm from "../components/AddWishListItemForm";
import EditWishListItemForm from "../components/EditWishListItemForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPencilAlt, faLink, faGripVertical, faPlus } from "@fortawesome/free-solid-svg-icons";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TruncatedText from "../components/TruncatedText";

const DragHandle = (props) => (
  <div {...props} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 touch-none">
    <FontAwesomeIcon icon={faGripVertical} />
  </div>
);

const WishListItem = ({ item, openEditModal, handleDeleteItem, dragHandleProps, isOverlay }) => {
  return (
    <div
      className={`card bg-white dark:bg-dark-surface flex items-start justify-between w-full transition-all duration-200 ${
        isOverlay ? "shadow-2xl scale-105 ring-2 ring-primary-500 rotate-1 z-50" : "hover:shadow-md"
      }`}
    >
      <div className="flex items-start w-full">
        {/* drag handle */}
        <div className="flex items-center justify-center pr-3 pt-1">
          <DragHandle {...dragHandleProps} />
        </div>

        {/* content area */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-base font-semibold text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 block truncate flex-1"
            >
              {item.name}
            </a>

            {/* actions */}
            <div className="flex items-center flex-shrink-0 space-x-1 relative z-10">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openEditModal(item);
                }}
                className="text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 p-1.5 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/30"
              >
                <FontAwesomeIcon icon={faPencilAlt} size="sm" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteItem(item._id);
                }}
                className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30"
              >
                <FontAwesomeIcon icon={faTrash} size="sm" />
              </button>
            </div>
          </div>


          {/* description row: image inline with text */}
          <div className="mt-2 flex items-start gap-3">
            {item.imageUrl && (
              <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100 dark:border-gray-700">
                <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
              </div>
            )}

            <div className="flex-1 min-w-0">
              {item.description && (
                <div className="text-sm text-gray-500 dark:text-gray-300 leading-relaxed">
                  <TruncatedText text={item.description} maxLength={100} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SortableItem = ({ item, openEditModal, handleDeleteItem }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 999 : "auto",
  };

  return (
    <li ref={setNodeRef} style={style} className="list-none group">
      <WishListItem
        item={item}
        openEditModal={openEditModal}
        handleDeleteItem={handleDeleteItem}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </li>
  );
};

function WishList() {
  const { t } = useTranslation();
  const [wishLists, setWishLists] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [items, setItems] = useState({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [isAddingList, setIsAddingList] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [renamingListId, setRenamingListId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const fetchWishLists = async () => {
      try {
        const response = await axios.get("/api/wish-lists");
        if (response.data.length === 0) {
          const serhiiList = await axios.post("/api/wish-lists", { name: "serhii" });
          const katerynaList = await axios.post("/api/wish-lists", { name: "kateryna" });
          const lists = [serhiiList.data, katerynaList.data];
          setWishLists(lists);
          setActiveTab(lists[0]._id);
        } else {
          setWishLists(response.data);
          setActiveTab(response.data[0]._id);
        }
      } catch (error) {
        console.error("Error fetching wish lists:", error);
      }
    };

    fetchWishLists();
  }, []);

  useEffect(() => {
    if (activeTab) {
      fetchItems(activeTab);
    }
  }, [activeTab]);

  const fetchItems = async (wishListId) => {
    try {
      const response = await axios.get(`/api/wish-lists/${wishListId}/items`);
      setItems((prevItems) => ({
        ...prevItems,
        [wishListId]: response.data,
      }));
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  const handleAddItem = async (itemData) => {
    try {
      const response = await axios.post(`/api/wish-lists/${activeTab}/items`, itemData);
      const newItem = response.data;
      setItems((prevItems) => ({
        ...prevItems,
        [activeTab]: [...(prevItems[activeTab] || []), newItem],
      }));
    } catch (error) {
      console.error("Error adding item:", error);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm(t("areYouSureYouWantToDeleteThisItem"))) {
      try {
        await axios.delete(`/api/wish-lists/items/${itemId}`);
        setItems((prevItems) => ({
          ...prevItems,
          [activeTab]: prevItems[activeTab].filter((item) => item._id !== itemId),
        }));
      } catch (error) {
        console.error("Error deleting item:", error);
      }
    }
  };

  const handleEditItem = async (itemData) => {
    try {
      const response = await axios.put(`/api/wish-lists/items/${itemData._id}`, itemData);
      const updatedItem = response.data;
      setItems((prevItems) => ({
        ...prevItems,
        [activeTab]: prevItems[activeTab].map((item) => (item._id === updatedItem._id ? updatedItem : item)),
      }));
    } catch (error) {
      console.error("Error updating item:", error);
    }
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setIsEditModalOpen(true);
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setItems((prevItems) => {
        const activeList = prevItems[activeTab];
        const oldIndex = activeList.findIndex((item) => item._id === active.id);
        const newIndex = activeList.findIndex((item) => item._id === over.id);
        const reorderedItems = arrayMove(activeList, oldIndex, newIndex);

        const orderedIds = reorderedItems.map((item) => item._id);
        axios.put(`/api/wish-lists/${activeTab}/items/order`, { orderedIds });

        return {
          ...prevItems,
          [activeTab]: reorderedItems,
        };
      });
    }
    setActiveId(null);
  };

  const handleAddList = async (name) => {
    if (name) {
      try {
        const response = await axios.post("/api/wish-lists", { name });
        setWishLists([...wishLists, response.data]);
        setActiveTab(response.data._id);
        setIsAddingList(false);
        setNewListName("");
      } catch (error) {
        console.error("Error adding wish list:", error);
      }
    }
  };

  const handleDeleteList = async (listId) => {
    const listToDelete = wishLists.find((list) => list._id === listId);
    if (window.confirm(t("areYouSureYouWantToDeleteThisList", { listName: t(listToDelete.name) }))) {
      try {
        await axios.delete(`/api/wish-lists/${listId}`);
        setWishLists(wishLists.filter((list) => list._id !== listId));
        setItems((prevItems) => {
          const newItems = { ...prevItems };
          delete newItems[listId];
          return newItems;
        });
        if (activeTab === listId) {
          setActiveTab(wishLists[0]?._id || null);
        }
      } catch (error) {
        console.error("Error deleting wish list:", error);
      }
    }
  };

  const handleShareList = async (listId) => {
    try {
      const response = await axios.post(`/api/wish-lists/${listId}/share`);
      const shareableLink = response.data.shareableLink;
      navigator.clipboard.writeText(`${window.location.origin}/wish-list/shared/${shareableLink}`);
      alert(t("copied"));
    } catch (error) {
      console.error("Error sharing wish list:", error);
    }
  };

  const startRenamingList = (list) => {
    setNewListName(list.name);
    setRenamingListId(list._id);
  };

  const handleRenameListSubmit = async (listId) => {
    if (newListName && newListName !== wishLists.find((l) => l._id === listId)?.name) {
      try {
        const response = await axios.put(`/api/wish-lists/${listId}`, { name: newListName });
        setWishLists(wishLists.map((list) => (list._id === listId ? response.data : list)));
      } catch (error) {
        console.error("Error renaming wish list:", error);
      }
    }
    setRenamingListId(null);
    setNewListName("");
  };

  const displayListName = (list) => {
    return t(list.name);
  };

  const activeItem = activeId ? (items[activeTab] || []).find((item) => item._id === activeId) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-3xl lg:max-w-5xl mx-auto space-y-6"
    >
      {/* Tabs */}
      <div className="flex items-center justify-between bg-white dark:bg-dark-surface rounded-2xl p-1 shadow-sm border border-gray-100 dark:border-gray-700 transition-colors">
        <div className="flex space-x-1 overflow-x-auto no-scrollbar flex-1 items-center">
          {wishLists.map((list) => (
            <button
              key={list._id}
              onClick={() => handleTabClick(list._id)}
              className={`relative px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                activeTab === list._id
                  ? "text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/30 shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              {activeTab === list._id && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute inset-0 bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 -z-10"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                />
              )}
              <span className="relative z-10">{displayListName(list)}</span>
            </button>
          ))}
          {isAddingList ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAddList(newListName);
              }}
              className="flex items-center gap-1 ml-2"
            >
              <input
                type="text"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder={t("name")}
                className="w-32 px-3 py-2 bg-gray-50 dark:bg-dark-bg border-none rounded-xl text-sm focus:ring-2 focus:ring-primary-100 outline-none dark:text-white transition-colors"
                autoFocus
                onBlur={() => {
                  if (!newListName) setIsAddingList(false);
                }}
              />
              <button type="submit" className="p-2 bg-primary-600 text-white rounded-xl shadow-sm">
                <FontAwesomeIcon icon={faPlus} size="sm" />
              </button>
            </form>
          ) : (
            <button
              onClick={() => setIsAddingList(true)}
              className="ml-2 p-2 text-primary-600 hover:bg-primary-50 rounded-xl transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          )}
        </div>
      </div>

      {isAddModalOpen && (
        <AddWishListItemForm
          wishListId={activeTab}
          onAddItem={handleAddItem}
          onClose={() => setIsAddModalOpen(false)}
        />
      )}

      {isEditModalOpen && (
        <EditWishListItemForm
          item={selectedItem}
          onEditItem={handleEditItem}
          onClose={() => setIsEditModalOpen(false)}
        />
      )}

      <div className="space-y-4">
        {wishLists.map((list) => (
          <div key={list._id} style={{ display: activeTab === list._id ? "block" : "none" }}>
            {/* List Actions */}
            <div className="flex justify-end gap-2 mb-4 relative z-10">
              <button onClick={() => setIsAddModalOpen(true)} className="btn-primary text-sm flex items-center gap-2">
                <FontAwesomeIcon icon={faPlus} /> {t("addNewItem")}
              </button>
              <div className="flex bg-white dark:bg-dark-surface rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-1 transition-colors">
                {renamingListId === list._id ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleRenameListSubmit(list._id);
                    }}
                    className="flex items-center"
                  >
                    <input
                      type="text"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      className="w-32 px-2 py-1 text-sm border-none focus:ring-0"
                      autoFocus
                      onBlur={() => handleRenameListSubmit(list._id)}
                    />
                  </form>
                ) : (
                  <button
                    onClick={() => startRenamingList(list)}
                    className="p-2 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors"
                  >
                    <FontAwesomeIcon icon={faPencilAlt} />
                  </button>
                )}
                <button
                  onClick={() => handleShareList(list._id)}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors"
                >
                  <FontAwesomeIcon icon={faLink} />
                </button>
                <button
                  onClick={() => handleDeleteList(list._id)}
                  className="p-2 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext items={items[activeTab] || []} strategy={verticalListSortingStrategy}>
                <ul className="space-y-3">
                  {(items[activeTab] || []).map((item) => (
                    <SortableItem
                      key={item._id}
                      item={item}
                      openEditModal={openEditModal}
                      handleDeleteItem={handleDeleteItem}
                    />
                  ))}
                </ul>
              </SortableContext>
              <DragOverlay>
                {activeId && activeItem ? (
                  <WishListItem
                    item={activeItem}
                    openEditModal={openEditModal}
                    handleDeleteItem={handleDeleteItem}
                    dragHandleProps={{}}
                    isOverlay
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default WishList;
